import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthFirebaseFunctionsService } from '@principle-theorem/ng-auth';
import { TestAccountStatusComponent } from '@principle-theorem/ng-principle';
import {
  EmptyStateComponent,
  InformationBoxComponent,
  PartnerService,
} from '@principle-theorem/ng-principle-shared';
import {
  IOrganisationAddDialogData,
  OrganisationAddDialogComponent,
  OrganisationAddFormData,
} from '@principle-theorem/ng-principle/pages';
import {
  BasicDialogService,
  DialogPresets,
  NgSharedModule,
} from '@principle-theorem/ng-shared';
import { Partner, PartnerTestAccount } from '@principle-theorem/principle-core';
import { IPartnerTestAccount } from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  HISTORY_DATE_TIME_FORMAT,
  slugify,
  snapshotDefined,
  sortByCreatedAt,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Component({
  selector: 'pr-test-account-list',
  imports: [
    CommonModule,
    MatListModule,
    MatCardModule,
    MatIconModule,
    EmptyStateComponent,
    NgSharedModule,
    TestAccountStatusComponent,
    InformationBoxComponent,
  ],
  templateUrl: './test-account-list.component.html',
  styleUrl: './test-account-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestAccountListComponent {
  testAccounts$: Observable<WithRef<IPartnerTestAccount>[]>;
  readonly dateFormat = HISTORY_DATE_TIME_FORMAT;
  isProduction = environment.production;

  testAccountTooltip =
    'A maximum of 3 test accounts can be created per partner.';
  addTestAccountDisabled$: Observable<boolean>;

  constructor(
    private _dialog: BasicDialogService,
    private _partner: PartnerService,
    private _snackBar: MatSnackBar,
    private _authFunctions: AuthFirebaseFunctionsService
  ) {
    this.testAccounts$ = this._partner.partner$.pipe(
      filterUndefined(),
      switchMap((partner) => Partner.testAccounts$(partner)),
      map((testAccounts) => testAccounts.sort(sortByCreatedAt))
    );

    this.addTestAccountDisabled$ = this.testAccounts$.pipe(
      map((testAccounts) => testAccounts.length >= 3)
    );
  }

  async addNewTestAccount(): Promise<void> {
    const user = await snapshotDefined(this._partner.user$);
    const demoSpaces: IOrganisationAddDialogData = {
      fileNames: ['default.json'],
      userName: user.name,
      userEmail: user.email,
    };

    const organisationData = await this._dialog
      .open<
        OrganisationAddDialogComponent,
        IOrganisationAddDialogData,
        OrganisationAddFormData
      >(
        OrganisationAddDialogComponent,
        DialogPresets.small({ data: demoSpaces })
      )
      .afterClosed()
      .toPromise();

    if (
      !organisationData ||
      !organisationData.name ||
      !organisationData.region ||
      !organisationData.seedFileName
    ) {
      return;
    }

    const alreadyExists: boolean =
      await this._authFunctions.checkOrganisationExists(
        slugify(organisationData.name.toLowerCase())
      );

    if (alreadyExists) {
      this._snackBar.open('Organisation with this name already exists');
      return;
    }

    const partner = await snapshotDefined(this._partner.partner$);

    await Firestore.addDoc(
      Partner.testAccountCol(partner),
      PartnerTestAccount.init({
        ...organisationData,
        userRef: user.ref,
      })
    );
    this._snackBar.open('Partner Test Account added');
  }
}
